import { useState, useEffect } from 'react'
import { Link, useParams, useNavigate } from 'react-router-dom'
import { analyticsApi, appsApi, billingApi, dashboardApi, alertsApi, errorsApi } from '../api'
import type { UsageResponse } from '../api/billing'
import type { IncidentSummary, RecentAlert, DashboardOverview, AlertHistory } from '../types'
import { useAuth } from '../lib/auth'
import type {
  DashboardSummary,
  ResponseTimeTrend,
  ErrorRateTrend,
  UptimeTrend,
  AnomalyDetectionWithApp,
  AnomalyContext,
  AppWithStats,
  UptimeAnalytics,
  ErrorGroup,
} from '../types'
import {
  BarChart3,
  AlertTriangle,
  Clock,
  TrendingUp,
  RefreshCw,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  Activity,
  Zap,
  Gauge,
  AppWindow,
  Bell,
  Mail,
  MessageSquare,
  Webhook,
  ExternalLink,
} from 'lucide-react'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
} from 'recharts'
import { Card, PageHeader, Skeleton, SimpleDashboardChart } from '../components'
import type { TimePeriod } from '../components'
import toast from 'react-hot-toast'

export default function DashboardPage() {
  const { teamId } = useParams<{ teamId: string }>()
  const navigate = useNavigate()
  const { user, currentTeam, isLoading: authLoading } = useAuth()
  const [apps, setApps] = useState<AppWithStats[]>([])
  const [selectedAppId, setSelectedAppId] = useState<string>('')
  const [summary, setSummary] = useState<DashboardSummary | null>(null)
  const [responseTimeTrends, setResponseTimeTrends] = useState<ResponseTimeTrend[]>([])
  const [errorRateTrends, setErrorRateTrends] = useState<ErrorRateTrend[]>([])
  const [uptimeAnalytics, setUptimeAnalytics] = useState<UptimeAnalytics | null>(null)
  const [uptimeTrends, setUptimeTrends] = useState<UptimeTrend[]>([])
  const [anomalies, setAnomalies] = useState<AnomalyDetectionWithApp[]>([])
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState<'overview' | 'performance' | 'anomalies' | 'alerts'>(
    'overview'
  )

  // Alerts tab state
  const [alertHistory, setAlertHistory] = useState<AlertHistory[]>([])
  const [alertsTotal, setAlertsTotal] = useState(0)
  const [alertsLoading, setAlertsLoading] = useState(false)
  const [alertsPage, setAlertsPage] = useState(0)
  const [expandedAnomalyId, setExpandedAnomalyId] = useState<string | null>(null)
  const [anomalyContext, setAnomalyContext] = useState<AnomalyContext | null>(null)
  const [loadingContext, setLoadingContext] = useState(false)
  const [hoveredErrorHour, setHoveredErrorHour] = useState<string | null>(null) // Stores first 13 chars of timestamp (hour-level)
  const [hoveredAlertTimestamp, setHoveredAlertTimestamp] = useState<string | null>(null)

  // Dashboard state
  const [overview, setOverview] = useState<DashboardOverview | null>(null)
  const [usage, setUsage] = useState<UsageResponse | null>(null)
  const [incidents, setIncidents] = useState<IncidentSummary[]>([])
  const [recentAlerts, setRecentAlerts] = useState<RecentAlert[]>([])
  const [overviewLoading, setOverviewLoading] = useState(true)

  // Overview chart data (14 days, aggregated across all apps or first app)
  const [overviewErrorTrends, setOverviewErrorTrends] = useState<ErrorRateTrend[]>([])
  const [overviewResponseTrends, setOverviewResponseTrends] = useState<ResponseTimeTrend[]>([])
  const [overviewUptimeTrends, setOverviewUptimeTrends] = useState<UptimeTrend[]>([])

  // Dashboard chart state
  const [overviewAppFilter, setOverviewAppFilter] = useState<string | null>(null)
  const [missionControlTimePeriod, setMissionControlTimePeriod] = useState<TimePeriod>('7d')
  const [missionControlLoading, setMissionControlLoading] = useState(false)
  const [recentErrorGroups, setRecentErrorGroups] = useState<ErrorGroup[]>([])

  // Helper to get hour key from timestamp (first 13 chars: "2024-01-15T14")
  const getHourKey = (timestamp: string) => timestamp?.substring(0, 13) || ''

  // Helper to format alert type to readable label
  const formatAlertType = (type: string) => {
    const typeLabels: Record<string, string> = {
      error: 'Error',
      ping_failure: 'Downtime',
      recovery: 'Recovery',
      metric_alert: 'Metric',
      anomaly: 'Anomaly',
    }
    return typeLabels[type] || type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
  }

  // Helper to get alert type color
  const getAlertTypeColor = (type: string) => {
    switch (type) {
      case 'error':
        return 'bg-red-100 text-red-700'
      case 'ping_failure':
        return 'bg-amber-100 text-amber-700'
      case 'recovery':
        return 'bg-green-100 text-green-700'
      case 'metric_alert':
        return 'bg-blue-100 text-blue-700'
      case 'anomaly':
        return 'bg-purple-100 text-purple-700'
      default:
        return 'bg-orange-100 text-orange-700'
    }
  }

  useEffect(() => {
    if (authLoading || !user || !currentTeam) return
    loadApps()
    loadSummary()
    loadAnomalies()
    loadOverviewData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTeam, authLoading, user])

  useEffect(() => {
    if (authLoading || !user) return
    if (selectedAppId) {
      loadAppAnalytics()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAppId, authLoading, user])

  // Load alerts when alerts tab is active or page changes
  useEffect(() => {
    if (authLoading || !user) return
    if (activeTab === 'alerts') {
      loadAlertHistory()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, alertsPage, authLoading, user])

  // Load Mission Control data when apps are loaded (handles race condition with loadOverviewData)
  useEffect(() => {
    if (authLoading || !user) return
    if (apps.length > 0 && overviewErrorTrends.length === 0 && !missionControlLoading) {
      // Default to "All Apps" (null) - aggregate data across all apps
      loadMissionControlData(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apps, authLoading, user])

  const loadAlertHistory = async () => {
    setAlertsLoading(true)
    try {
      const response = await alertsApi.getHistory({
        skip: alertsPage * 20,
        limit: 20,
      })
      setAlertHistory(response.alerts)
      setAlertsTotal(response.total)
    } catch (err) {
      console.error('Failed to load alerts:', err)
    } finally {
      setAlertsLoading(false)
    }
  }

  const loadApps = async () => {
    try {
      const response = await appsApi.list()
      setApps(response.apps)
      if (response.apps.length > 0) {
        setSelectedAppId(response.apps[0].id)
      }
    } catch {
      toast.error('Failed to load apps')
    }
  }

  const loadSummary = async () => {
    try {
      const data = await analyticsApi.getDashboardSummary()
      setSummary(data)
    } catch {
      toast.error('Failed to load dashboard summary')
    }
  }

  const loadAppAnalytics = async () => {
    if (!selectedAppId) return
    setLoading(true)
    try {
      const [responseTrends, errorTrends, uptime, uptimeTrendsData] = await Promise.all([
        analyticsApi.getResponseTimeTrends(selectedAppId, 30, 'day'),
        analyticsApi.getErrorRateTrends(selectedAppId, 30, 'day'),
        analyticsApi.getUptimeAnalytics(selectedAppId, 30),
        analyticsApi.getUptimeTrends(selectedAppId, 30, 'day'),
      ])
      setResponseTimeTrends(responseTrends)
      setErrorRateTrends(errorTrends)
      setUptimeAnalytics(uptime)
      setUptimeTrends(uptimeTrendsData)
    } catch {
      toast.error('Failed to load app analytics')
    } finally {
      setLoading(false)
    }
  }

  const loadAnomalies = async () => {
    try {
      const response = await analyticsApi.getAnomalies(undefined, 7, false)
      setAnomalies(response.anomalies)
    } catch {
      toast.error('Failed to load anomalies')
    }
  }

  const loadOverviewData = async () => {
    setOverviewLoading(true)
    try {
      const [overviewData, incidentsData, alertsData] = await Promise.all([
        dashboardApi.getOverview(),
        dashboardApi.getActiveIncidents(),
        dashboardApi.getRecentAlerts(5),
      ])
      setOverview(overviewData)
      setIncidents(incidentsData.incidents)
      setRecentAlerts(alertsData.alerts)

      // Load usage (needs team context)
      if (currentTeam) {
        try {
          const usageData = await billingApi.getUsage(currentTeam.id)
          setUsage(usageData)
        } catch {
          // Usage fetch is optional
        }
      }

      // Load overview charts for all apps (aggregated view)
      if (overviewData.total_apps > 0 && apps.length > 0) {
        await loadMissionControlData(null)
      }
    } catch (err) {
      console.error('Failed to load overview data:', err)
    } finally {
      setOverviewLoading(false)
    }
  }

  // Load Mission Control data based on time period and selected app
  const loadMissionControlData = async (
    appId?: string | null,
    period: TimePeriod = missionControlTimePeriod
  ) => {
    setMissionControlLoading(true)
    try {
      const timePeriodHours: Record<TimePeriod, number> = {
        '24h': 24,
        '7d': 168,
        '30d': 720,
      }
      const days = Math.ceil((timePeriodHours[period] || 168) / 24)
      const granularity = days <= 1 ? 'hour' : 'day'

      const targetAppId = appId || (apps.length > 0 ? apps[0].id : null)
      if (!targetAppId) return

      // If "All Apps" selected (appId is null), aggregate data from all apps
      // Otherwise query just the selected app
      let errorTrends: ErrorRateTrend[] = []
      let responseTrends: ResponseTimeTrend[] = []
      let uptimeTrends: UptimeTrend[] = []

      if (!appId && apps.length > 0) {
        // Aggregate across all apps for "All Apps" view
        const allResults = await Promise.all(
          apps.map(app =>
            Promise.all([
              analyticsApi.getErrorRateTrends(app.id, days, granularity),
              analyticsApi.getResponseTimeTrends(app.id, days, granularity),
              analyticsApi.getUptimeTrends(app.id, days, granularity),
            ])
          )
        )

        // Merge error trends - aggregate by timestamp
        const errorByTimestamp = new Map<string, number>()
        allResults.forEach(([appErrors]) => {
          appErrors.forEach(e => {
            if (e.timestamp) {
              const current = errorByTimestamp.get(e.timestamp) || 0
              errorByTimestamp.set(e.timestamp, current + (e.error_count || 0))
            }
          })
        })
        errorTrends = Array.from(errorByTimestamp.entries())
          .map(([timestamp, error_count]) => ({ timestamp, error_count, unique_errors: 0 }))
          .sort((a, b) => a.timestamp.localeCompare(b.timestamp))

        // Merge response trends - average by timestamp
        const latencyByTimestamp = new Map<string, { total: number; count: number }>()
        allResults.forEach(([, appLatency]) => {
          appLatency.forEach(l => {
            if (l.timestamp && l.avg_response_time) {
              const current = latencyByTimestamp.get(l.timestamp) || { total: 0, count: 0 }
              latencyByTimestamp.set(l.timestamp, {
                total: current.total + l.avg_response_time,
                count: current.count + 1,
              })
            }
          })
        })
        responseTrends = Array.from(latencyByTimestamp.entries())
          .map(([timestamp, { total, count }]) => ({
            timestamp,
            avg_response_time: total / count,
            min_response_time: 0,
            max_response_time: 0,
            p95_response_time: null,
            sample_count: 0,
          }))
          .sort((a, b) => a.timestamp.localeCompare(b.timestamp))

        // Merge uptime trends - average by timestamp
        const uptimeByTimestamp = new Map<string, { total: number; count: number }>()
        allResults.forEach(([, , appUptime]) => {
          appUptime.forEach(u => {
            if (u.timestamp && u.uptime_percentage != null) {
              const current = uptimeByTimestamp.get(u.timestamp) || { total: 0, count: 0 }
              uptimeByTimestamp.set(u.timestamp, {
                total: current.total + u.uptime_percentage,
                count: current.count + 1,
              })
            }
          })
        })
        uptimeTrends = Array.from(uptimeByTimestamp.entries())
          .map(([timestamp, { total, count }]) => ({
            timestamp,
            uptime_percentage: total / count,
            successful_pings: 0,
            total_pings: 0,
          }))
          .sort((a, b) => a.timestamp.localeCompare(b.timestamp))
      } else {
        // Single app query
        const results = await Promise.all([
          analyticsApi.getErrorRateTrends(targetAppId, days, granularity),
          analyticsApi.getResponseTimeTrends(targetAppId, days, granularity),
          analyticsApi.getUptimeTrends(targetAppId, days, granularity),
        ])
        errorTrends = results[0]
        responseTrends = results[1]
        uptimeTrends = results[2]
      }

      setOverviewErrorTrends(errorTrends)
      setOverviewResponseTrends(responseTrends)
      setOverviewUptimeTrends(uptimeTrends)

      // Fetch recent error groups if a specific app is selected
      if (appId) {
        try {
          const errorGroupsResponse = await errorsApi.listGroups(appId, {
            limit: 5,
            status: 'open',
          })
          setRecentErrorGroups(errorGroupsResponse.error_groups)
        } catch {
          setRecentErrorGroups([])
        }
      } else {
        setRecentErrorGroups([])
      }
    } catch {
      // Chart data is optional - silently fail
    } finally {
      setMissionControlLoading(false)
    }
  }

  // Handle mission control time period change
  const handleMissionControlTimePeriodChange = (period: TimePeriod) => {
    setMissionControlTimePeriod(period)
    loadMissionControlData(overviewAppFilter, period)
  }

  // Handle mission control app filter change
  const handleMissionControlAppChange = (appId: string | null) => {
    setOverviewAppFilter(appId)
    loadMissionControlData(appId, missionControlTimePeriod)
  }

  const acknowledgeAnomaly = async (anomalyId: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      await analyticsApi.acknowledgeAnomaly(anomalyId)
      setAnomalies(anomalies.filter(a => a.id !== anomalyId))
      if (expandedAnomalyId === anomalyId) {
        setExpandedAnomalyId(null)
        setAnomalyContext(null)
      }
      toast.success('Anomaly acknowledged')
    } catch {
      toast.error('Failed to acknowledge anomaly')
    }
  }

  const toggleAnomalyExpand = async (anomalyId: string) => {
    if (expandedAnomalyId === anomalyId) {
      // Collapse
      setExpandedAnomalyId(null)
      setAnomalyContext(null)
    } else {
      // Expand and load context
      setExpandedAnomalyId(anomalyId)
      setLoadingContext(true)
      try {
        const context = await analyticsApi.getAnomalyContext(anomalyId)
        setAnomalyContext(context)
      } catch {
        toast.error('Failed to load anomaly details')
        setExpandedAnomalyId(null)
      } finally {
        setLoadingContext(false)
      }
    }
  }

  const formatDuration = (seconds: number) => {
    if (seconds < 60) return `${seconds}s`
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m`
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
  }

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'text-red-500 bg-red-500/10'
      case 'high':
        return 'text-orange-500 bg-orange-500/10'
      case 'medium':
        return 'text-yellow-500 bg-yellow-500/10'
      case 'low':
        return 'text-blue-500 bg-blue-500/10'
      default:
        return 'text-slate-500 bg-slate-500/10'
    }
  }

  // Prepare chart data with error and alert markers
  const prepareAnomalyChartData = () => {
    if (!anomalyContext) return { chartData: [], errorData: [], alertData: [] }

    // Filter out invalid points, map to chart format, then sort by time
    const chartData = [...(anomalyContext.metric_trends || [])]
      .filter(point => point.timestamp && point.value != null)
      .map(point => ({
        timestamp: point.timestamp,
        time: new Date(point.timestamp).getTime(),
        value: point.value,
      }))
      .sort((a, b) => a.time - b.time)

    // Prepare error data for scatter plot (position on chart using the Y value at that time)
    const errorData = (anomalyContext.error_events || [])
      .map(event => {
        // Find matching metric point or use upper bound for visibility
        const matchingPoint = (anomalyContext.metric_trends || []).find(
          p => p.timestamp?.substring(0, 13) === event.timestamp?.substring(0, 13)
        )
        return {
          timestamp: event.timestamp,
          time: new Date(event.timestamp).getTime(),
          y: matchingPoint?.value ?? anomalyContext.upper_bound,
          count: event.count,
          level: event.level,
        }
      })
      .sort((a, b) => a.time - b.time)

    // Prepare alert data for scatter plot
    const alertData = (anomalyContext.alert_events || [])
      .map(event => {
        const matchingPoint = (anomalyContext.metric_trends || []).find(
          p => p.timestamp?.substring(0, 13) === event.timestamp?.substring(0, 13)
        )
        return {
          timestamp: event.timestamp,
          time: new Date(event.timestamp).getTime(),
          y: matchingPoint?.value ?? anomalyContext.upper_bound,
          type: event.type,
          title: event.title,
        }
      })
      .sort((a, b) => a.time - b.time)

    return { chartData, errorData, alertData }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title={`Welcome back${user?.name ? `, ${user.name}` : ''}!`}
        subtitle="Monitor your applications' health and performance"
        action={
          <button
            onClick={() => {
              loadSummary()
              loadAnomalies()
              loadOverviewData()
              if (selectedAppId) loadAppAnalytics()
            }}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-900 hover:bg-slate-50 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
        }
      />

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-8">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'performance', label: 'Performance', icon: TrendingUp },
            { id: 'anomalies', label: 'Anomalies', icon: AlertTriangle },
            { id: 'alerts', label: 'Alerts', icon: Bell },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as typeof activeTab)}
              className={`flex items-center gap-2 pb-4 border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-500 text-slate-900'
                  : 'border-transparent text-slate-500 hover:text-slate-900'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
              {tab.id === 'anomalies' && anomalies.length > 0 && (
                <span className="px-2 py-0.5 text-xs bg-red-100 text-red-600 rounded-full">
                  {anomalies.length}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          {overviewLoading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[...Array(4)].map((_, i) => (
                <Card key={i} variant="stat" className="p-4">
                  <Skeleton className="h-4 w-20 mb-2" />
                  <Skeleton className="h-8 w-12" />
                </Card>
              ))}
            </div>
          ) : (
            overview && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card variant="stat" className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <AppWindow className="w-4 h-4 text-slate-400" />
                    <p className="text-sm text-slate-500">Total Apps</p>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">{overview.total_apps}</p>
                </Card>
                <Card variant="stat" className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <XCircle className="w-4 h-4 text-slate-400" />
                    <p className="text-sm text-slate-500">Errors (24h)</p>
                  </div>
                  <p
                    className={`text-2xl font-bold ${overview.total_errors_24h > 0 ? 'text-red-600' : 'text-slate-900'}`}
                  >
                    {overview.total_errors_24h}
                  </p>
                </Card>
                <Card variant="stat" className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <Zap className="w-4 h-4 text-slate-400" />
                    <p className="text-sm text-slate-500">Anomalies (24h)</p>
                  </div>
                  <p
                    className={`text-2xl font-bold ${overview.anomaly_count_24h > 0 ? 'text-amber-600' : 'text-slate-900'}`}
                  >
                    {overview.anomaly_count_24h}
                  </p>
                </Card>
                <Card variant="stat" className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <CheckCircle className="w-4 h-4 text-slate-400" />
                    <p className="text-sm text-slate-500">Overall Uptime</p>
                  </div>
                  <p className="text-2xl font-bold text-slate-900">
                    {overview.overall_uptime !== null
                      ? `${overview.overall_uptime.toFixed(1)}%`
                      : '-'}
                  </p>
                </Card>
                <Card variant="stat" className="p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="w-4 h-4 text-slate-400" />
                    <p className="text-sm text-slate-500">Apps with Issues</p>
                  </div>
                  <p
                    className={`text-2xl font-bold ${overview.apps_with_issues > 0 ? 'text-yellow-600' : 'text-slate-900'}`}
                  >
                    {overview.apps_with_issues}
                  </p>
                </Card>
              </div>
            )
          )}

          {/* Error Quota Usage Bar - only show if there's a limit */}
          {usage && usage.max_errors_monthly != null && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Gauge className="w-5 h-5 text-slate-500" />
                  <span className="font-medium text-slate-900">Monthly Error Quota</span>
                </div>
                <span className="text-sm text-slate-600">
                  {usage.errors_this_month.toLocaleString()} /{' '}
                  {usage.max_errors_monthly.toLocaleString()} errors
                </span>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    usage.errors_this_month >= usage.max_errors_monthly
                      ? 'bg-red-500'
                      : usage.errors_this_month >= usage.max_errors_monthly * 0.9
                        ? 'bg-amber-500'
                        : usage.errors_this_month >= usage.max_errors_monthly * 0.8
                          ? 'bg-yellow-500'
                          : 'bg-primary-500'
                  }`}
                  style={{
                    width: `${Math.min(100, (usage.errors_this_month / usage.max_errors_monthly) * 100)}%`,
                  }}
                />
              </div>
              {usage.errors_this_month >= usage.max_errors_monthly * 0.8 && (
                <p
                  className={`text-xs mt-2 ${
                    usage.errors_this_month >= usage.max_errors_monthly
                      ? 'text-red-600'
                      : 'text-amber-600'
                  }`}
                >
                  {usage.errors_this_month >= usage.max_errors_monthly
                    ? 'Quota exceeded. New errors will be rejected.'
                    : `${Math.round((usage.errors_this_month / usage.max_errors_monthly) * 100)}% used - consider upgrading your plan.`}{' '}
                  <Link to={`/t/${teamId}/settings`} className="underline hover:no-underline">
                    Manage billing
                  </Link>
                </p>
              )}
            </Card>
          )}

          {/* Active Incidents */}
          {incidents.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h3 className="font-semibold text-red-600">Active Incidents</h3>
              </div>
              <div className="space-y-2">
                {incidents.slice(0, 3).map(incident => (
                  <Link
                    key={incident.id}
                    to={`/t/${teamId}/apps/${incident.app_id}`}
                    className="flex items-center justify-between text-sm hover:bg-red-100 rounded-lg px-2 py-1.5 -mx-2 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-red-600" />
                      <span className="text-slate-900">{incident.app_name}</span>
                      <span className="text-slate-600">- {incident.cause}</span>
                    </div>
                    <span className="text-slate-500">
                      {new Date(incident.created_at).toLocaleString()}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Dashboard Chart - Simplified View */}
          <SimpleDashboardChart
            apps={apps}
            errorTrends={overviewErrorTrends}
            responseTrends={overviewResponseTrends}
            uptimeTrends={overviewUptimeTrends}
            selectedAppId={overviewAppFilter}
            onAppChange={handleMissionControlAppChange}
            timePeriod={missionControlTimePeriod}
            onTimePeriodChange={handleMissionControlTimePeriodChange}
            loading={missionControlLoading}
            recentErrorGroups={recentErrorGroups}
            onViewAllErrors={
              overviewAppFilter
                ? () => {
                    navigate(`/t/${teamId}/apps/${overviewAppFilter}`)
                  }
                : undefined
            }
            onErrorClick={(appId, errorGroupId) => {
              navigate(`/t/${teamId}/apps/${appId}/errors/${errorGroupId}`)
            }}
          />

          {/* Recent Alerts */}
          {recentAlerts.length > 0 && (
            <Card className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-slate-900">Recent Alerts</h3>
                <button
                  onClick={() => setActiveTab('alerts')}
                  className="text-sm text-primary-600 hover:text-primary-700"
                >
                  View all
                </button>
              </div>
              <div className="space-y-2">
                {recentAlerts.map(alert => (
                  <div key={alert.id} className="flex items-center justify-between text-sm">
                    <span className="text-slate-900 truncate">{alert.subject}</span>
                    <span className="text-slate-500 text-xs">
                      {new Date(alert.sent_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </Card>
          )}

          {/* Show summary stats if we have them (from original analytics) */}
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span className="text-sm text-slate-600">Avg Response (24h)</span>
                </div>
                <div className="text-xl font-bold text-slate-900">
                  {summary.avg_response_time_24h
                    ? `${summary.avg_response_time_24h.toFixed(0)}ms`
                    : 'N/A'}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <XCircle className="w-4 h-4 text-yellow-500" />
                  <span className="text-sm text-slate-600">Errors (7d)</span>
                </div>
                <div className="text-xl font-bold text-slate-900">{summary.total_errors_7d}</div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="text-sm text-slate-600">Avg Uptime (7d)</span>
                </div>
                <div className="text-xl font-bold text-slate-900">
                  {summary.avg_uptime_7d ? `${summary.avg_uptime_7d.toFixed(2)}%` : 'N/A'}
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-center gap-2 mb-2">
                  <Zap className="w-4 h-4 text-purple-500" />
                  <span className="text-sm text-slate-600">Active Incidents</span>
                </div>
                <div className="text-xl font-bold text-slate-900">{summary.active_incidents}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Performance Tab */}
      {activeTab === 'performance' && (
        <div className="space-y-6">
          {/* App Selector */}
          <div className="flex items-center gap-4">
            <label className="text-sm text-slate-600">Select App:</label>
            <select
              value={selectedAppId}
              onChange={e => setSelectedAppId(e.target.value)}
              className="px-4 py-2 bg-white border border-slate-200 rounded-lg text-slate-900"
            >
              {apps.map(app => (
                <option key={app.id} value={app.id}>
                  {app.name}
                </option>
              ))}
            </select>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary-500"></div>
            </div>
          ) : (
            <>
              {/* Uptime Stats */}
              {uptimeAnalytics && (
                <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                  <h3 className="text-lg font-semibold text-slate-900 mb-4">
                    Uptime Analytics (30d)
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <div className="text-2xl font-bold text-slate-900">
                        {uptimeAnalytics.uptime_percentage?.toFixed(2)}%
                      </div>
                      <div className="text-sm text-slate-500">Uptime</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-900">
                        {uptimeAnalytics.incident_count}
                      </div>
                      <div className="text-sm text-slate-500">Incidents</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-900">
                        {uptimeAnalytics.mttr_seconds
                          ? formatDuration(uptimeAnalytics.mttr_seconds)
                          : 'N/A'}
                      </div>
                      <div className="text-sm text-slate-500">MTTR</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-slate-900">
                        {formatDuration(uptimeAnalytics.total_downtime_seconds)}
                      </div>
                      <div className="text-sm text-slate-500">Total Downtime</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Combined Performance Trends (30 days) */}
              {(responseTimeTrends.length > 0 ||
                errorRateTrends.length > 0 ||
                uptimeTrends.length > 0) &&
                (() => {
                  // Merge all 30-day data by index
                  const mergedData = responseTimeTrends.map((respPoint, i) => ({
                    timestamp: respPoint.timestamp,
                    latency: respPoint.avg_response_time || 0,
                    errors: errorRateTrends[i]?.error_count || 0,
                    uptime: uptimeTrends[i]?.uptime_percentage || 100,
                  }))

                  const totalErrors = errorRateTrends.reduce(
                    (sum, d) => sum + (d.error_count || 0),
                    0
                  )
                  const avgLatency =
                    responseTimeTrends.length > 0
                      ? Math.round(
                          responseTimeTrends.reduce(
                            (sum, d) => sum + (d.avg_response_time || 0),
                            0
                          ) / responseTimeTrends.length
                        )
                      : 0
                  const avgUptime =
                    uptimeTrends.length > 0
                      ? (
                          uptimeTrends.reduce((sum, d) => sum + (d.uptime_percentage || 0), 0) /
                          uptimeTrends.length
                        ).toFixed(2)
                      : '100'

                  return (
                    <Card className="p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold text-slate-900">
                          Performance Trends (30 days)
                        </h3>
                        <div className="flex items-center gap-5 text-sm">
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-0.5 bg-red-500 rounded" />
                            <span className="text-slate-500">Errors:</span>
                            <span className="font-semibold text-slate-900">{totalErrors}</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-0.5 bg-blue-500 rounded" />
                            <span className="text-slate-500">Latency:</span>
                            <span className="font-semibold text-slate-900">{avgLatency}ms</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <div className="w-3 h-0.5 bg-green-500 rounded" />
                            <span className="text-slate-500">Uptime:</span>
                            <span className="font-semibold text-slate-900">{avgUptime}%</span>
                          </div>
                        </div>
                      </div>
                      <div className="h-48">
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={mergedData}
                            margin={{ top: 5, right: 10, left: 10, bottom: 5 }}
                          >
                            <CartesianGrid
                              strokeDasharray="3 3"
                              stroke="#e2e8f0"
                              vertical={false}
                            />
                            <XAxis
                              dataKey="timestamp"
                              tick={{ fontSize: 10, fill: '#94a3b8' }}
                              tickFormatter={value =>
                                new Date(value).toLocaleDateString('en-US', {
                                  month: 'short',
                                  day: 'numeric',
                                })
                              }
                              axisLine={{ stroke: '#e2e8f0' }}
                              tickLine={false}
                            />
                            <YAxis yAxisId="left" hide />
                            <YAxis yAxisId="right" hide />
                            <Tooltip
                              contentStyle={{
                                backgroundColor: '#fff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                fontSize: 12,
                              }}
                              labelFormatter={value =>
                                new Date(value).toLocaleDateString('en-US', {
                                  weekday: 'short',
                                  month: 'short',
                                  day: 'numeric',
                                })
                              }
                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              formatter={(value: any, name: any) => {
                                if (value == null) return ['-', name]
                                if (name === 'errors') return [value, 'Errors']
                                if (name === 'latency')
                                  return [`${Number(value).toFixed(0)}ms`, 'Latency']
                                if (name === 'uptime')
                                  return [`${Number(value).toFixed(2)}%`, 'Uptime']
                                return [value, name]
                              }}
                            />
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="errors"
                              stroke="#ef4444"
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 4, fill: '#ef4444' }}
                            />
                            <Line
                              yAxisId="left"
                              type="monotone"
                              dataKey="latency"
                              stroke="#3b82f6"
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 4, fill: '#3b82f6' }}
                            />
                            <Line
                              yAxisId="right"
                              type="monotone"
                              dataKey="uptime"
                              stroke="#22c55e"
                              strokeWidth={2}
                              dot={false}
                              activeDot={{ r: 4, fill: '#22c55e' }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    </Card>
                  )
                })()}
            </>
          )}
        </div>
      )}

      {/* Anomalies Tab */}
      {activeTab === 'anomalies' && (
        <div className="space-y-4">
          {anomalies.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <AlertTriangle className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No unacknowledged anomalies</p>
            </div>
          ) : (
            anomalies.map(anomaly => {
              const isExpanded = expandedAnomalyId === anomaly.id
              return (
                <div
                  key={anomaly.id}
                  className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden"
                >
                  {/* Anomaly Header - Clickable */}
                  <div
                    onClick={() => toggleAnomalyExpand(anomaly.id)}
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded ${getSeverityColor(anomaly.severity)}`}
                      >
                        {anomaly.severity.toUpperCase()}
                      </span>
                      <div>
                        <div className="text-slate-900 font-medium">{anomaly.app_name}</div>
                        <div className="text-sm text-slate-600">
                          {anomaly.metric_name === 'response_time' ? 'Response Time' : 'Error Rate'}
                          : {anomaly.detected_value.toFixed(2)}
                          {anomaly.metric_name === 'response_time' ? 'ms' : ' errors'}
                          <span className="text-slate-400 mx-1">•</span>
                          {anomaly.deviation.toFixed(1)}σ from baseline
                        </div>
                        <div className="text-xs text-slate-500">
                          {new Date(anomaly.created_at).toLocaleString()}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={e => acknowledgeAnomaly(anomaly.id, e)}
                        className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded transition-colors"
                      >
                        Acknowledge
                      </button>
                      {isExpanded ? (
                        <ChevronUp className="w-5 h-5 text-slate-400" />
                      ) : (
                        <ChevronDown className="w-5 h-5 text-slate-400" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Content */}
                  {isExpanded && (
                    <div className="border-t border-slate-200 p-4 bg-slate-50">
                      {loadingContext ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-primary-500"></div>
                        </div>
                      ) : anomalyContext ? (
                        <div className="space-y-4">
                          {/* Context Stats */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-white rounded-lg p-3 border border-slate-200">
                              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                <Activity className="w-3 h-3" />
                                Detected Value
                              </div>
                              <div className="text-lg font-semibold text-slate-900">
                                {anomalyContext.detected_value.toFixed(1)}
                                {anomalyContext.metric_name === 'response_time' ? 'ms' : ''}
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-slate-200">
                              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                <TrendingUp className="w-3 h-3" />
                                Baseline
                              </div>
                              <div className="text-lg font-semibold text-slate-900">
                                {anomalyContext.baseline_value.toFixed(1)}
                                {anomalyContext.metric_name === 'response_time' ? 'ms' : ''}
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-slate-200">
                              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                <XCircle className="w-3 h-3" />
                                Errors (±1h)
                              </div>
                              <div className="text-lg font-semibold text-slate-900">
                                {anomalyContext.error_count_window}
                              </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 border border-slate-200">
                              <div className="flex items-center gap-2 text-slate-500 text-xs mb-1">
                                <Zap className="w-3 h-3" />
                                Deviation
                              </div>
                              <div className="text-lg font-semibold text-slate-900">
                                {anomalyContext.deviation.toFixed(1)}σ
                              </div>
                            </div>
                          </div>

                          {/* Multi-lane Chart: Metric + Errors + Alerts */}
                          {(() => {
                            const { chartData, errorData, alertData } = prepareAnomalyChartData()
                            const minTime =
                              chartData.length > 0 ? Math.min(...chartData.map(d => d.time)) : 0
                            const maxTime =
                              chartData.length > 0 ? Math.max(...chartData.map(d => d.time)) : 0

                            // Error lane data - normalize to single Y value for display
                            const errorLaneData = errorData.map(err => ({
                              ...err,
                              y: 1, // Fixed y position for error lane
                            }))

                            // Alert lane data
                            const alertLaneData = alertData.map(alert => ({
                              ...alert,
                              y: 1, // Fixed y position for alert lane
                            }))

                            return (
                              <div className="bg-white rounded-lg p-4 border border-slate-200">
                                <h4 className="text-sm font-medium text-slate-900 mb-3">
                                  {anomalyContext.metric_name === 'response_time'
                                    ? 'Response Time'
                                    : 'Error Rate'}{' '}
                                  (48h window)
                                </h4>

                                {/* Main Metric Chart */}
                                <div style={{ height: 160 }}>
                                  <ResponsiveContainer width="100%" height="100%">
                                    <LineChart
                                      data={chartData}
                                      margin={{ top: 5, right: 5, left: 5, bottom: 0 }}
                                    >
                                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                                      <XAxis
                                        dataKey="time"
                                        type="number"
                                        domain={[minTime, maxTime]}
                                        tick={false}
                                        axisLine={{ stroke: '#e2e8f0' }}
                                      />
                                      <YAxis tick={{ fontSize: 10, fill: '#64748b' }} width={40} />
                                      <Tooltip
                                        contentStyle={{
                                          backgroundColor: '#fff',
                                          border: '1px solid #e2e8f0',
                                          borderRadius: '8px',
                                          fontSize: 12,
                                        }}
                                        labelFormatter={value => new Date(value).toLocaleString()}
                                      />
                                      <ReferenceArea
                                        y1={anomalyContext.lower_bound}
                                        y2={anomalyContext.upper_bound}
                                        fill="#22c55e"
                                        fillOpacity={0.1}
                                      />
                                      <ReferenceLine
                                        y={anomalyContext.baseline_value}
                                        stroke="#22c55e"
                                        strokeDasharray="5 5"
                                        label={{ value: 'Baseline', fontSize: 10, fill: '#22c55e' }}
                                      />
                                      <ReferenceLine
                                        y={anomalyContext.detected_value}
                                        stroke="#ef4444"
                                        strokeDasharray="3 3"
                                        label={{ value: 'Anomaly', fontSize: 10, fill: '#ef4444' }}
                                      />
                                      <Line
                                        type="monotone"
                                        dataKey="value"
                                        stroke="#3b82f6"
                                        strokeWidth={2}
                                        dot={{
                                          r: 3,
                                          fill: '#3b82f6',
                                          stroke: '#fff',
                                          strokeWidth: 1,
                                        }}
                                        activeDot={{
                                          r: 5,
                                          fill: '#3b82f6',
                                          stroke: '#fff',
                                          strokeWidth: 2,
                                        }}
                                        name={
                                          anomalyContext.metric_name === 'response_time'
                                            ? 'Response Time (ms)'
                                            : 'Error Count'
                                        }
                                      />
                                    </LineChart>
                                  </ResponsiveContainer>
                                </div>

                                {/* Error Lane - separate chart for hoverable error dots */}
                                {errorData.length > 0 && (
                                  <div style={{ height: 36 }} className="border-t border-slate-100">
                                    <div className="flex items-center h-full">
                                      <div className="w-[40px] flex-shrink-0 text-[9px] text-slate-400 text-right pr-1">
                                        Errors
                                      </div>
                                      <div className="flex-1 h-full relative">
                                        {/* Error dots positioned along the lane */}
                                        {errorLaneData.map((err, i) => {
                                          const hourKey = getHourKey(err.timestamp)
                                          const isHovered = hoveredErrorHour === hourKey
                                          const xPercent =
                                            ((err.time - minTime) / (maxTime - minTime)) * 100
                                          return (
                                            <div
                                              key={`error-dot-${i}`}
                                              className="absolute cursor-pointer transition-all duration-150"
                                              style={{
                                                left: `${xPercent}%`,
                                                top: '50%',
                                                transform: 'translate(-50%, -50%)',
                                                width: isHovered ? 16 : 10,
                                                height: isHovered ? 16 : 10,
                                                borderRadius: '50%',
                                                backgroundColor: isHovered ? '#dc2626' : '#ef4444',
                                                border: `2px solid ${isHovered ? '#fca5a5' : '#fff'}`,
                                                boxShadow: isHovered
                                                  ? '0 0 0 3px rgba(239, 68, 68, 0.2)'
                                                  : '0 1px 2px rgba(0,0,0,0.1)',
                                                zIndex: isHovered ? 10 : 1,
                                              }}
                                              onMouseEnter={() => setHoveredErrorHour(hourKey)}
                                              onMouseLeave={() => setHoveredErrorHour(null)}
                                              title={`${err.count} error(s) at ${new Date(err.timestamp).toLocaleTimeString()}`}
                                            />
                                          )
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* Alert Lane - separate chart for alert markers */}
                                {alertData.length > 0 && (
                                  <div style={{ height: 36 }} className="border-t border-slate-100">
                                    <div className="flex items-center h-full">
                                      <div className="w-[40px] flex-shrink-0 text-[9px] text-slate-400 text-right pr-1">
                                        Alerts
                                      </div>
                                      <div className="flex-1 h-full relative">
                                        {alertLaneData.map((alert, i) => {
                                          const isHovered =
                                            hoveredAlertTimestamp === alert.timestamp
                                          const xPercent =
                                            ((alert.time - minTime) / (maxTime - minTime)) * 100
                                          return (
                                            <div
                                              key={`alert-dot-${i}`}
                                              className="absolute cursor-pointer transition-all duration-150"
                                              style={{
                                                left: `${xPercent}%`,
                                                top: '50%',
                                                transform: `translate(-50%, -50%) rotate(45deg)`,
                                                width: isHovered ? 14 : 10,
                                                height: isHovered ? 14 : 10,
                                                backgroundColor: isHovered ? '#ea580c' : '#f97316',
                                                border: `2px solid ${isHovered ? '#fed7aa' : '#fff'}`,
                                                boxShadow: isHovered
                                                  ? '0 0 0 3px rgba(249, 115, 22, 0.2)'
                                                  : '0 1px 2px rgba(0,0,0,0.1)',
                                                zIndex: isHovered ? 10 : 1,
                                              }}
                                              onMouseEnter={() =>
                                                setHoveredAlertTimestamp(alert.timestamp)
                                              }
                                              onMouseLeave={() => setHoveredAlertTimestamp(null)}
                                              title={`${alert.type}: ${alert.title}`}
                                            />
                                          )
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                )}

                                {/* X-axis labels */}
                                <div className="flex" style={{ marginLeft: 40 }}>
                                  <div className="flex-1 flex justify-between text-[9px] text-slate-400 pt-1">
                                    {chartData.length > 0 &&
                                      (() => {
                                        const ticks = [0, 0.25, 0.5, 0.75, 1]
                                        return ticks.map((pct, i) => {
                                          const time = minTime + (maxTime - minTime) * pct
                                          const date = new Date(time)
                                          return (
                                            <span key={i}>
                                              {date.getMonth() + 1}/{date.getDate()}{' '}
                                              {String(date.getHours()).padStart(2, '0')}:
                                              {String(date.getMinutes()).padStart(2, '0')}
                                            </span>
                                          )
                                        })
                                      })()}
                                  </div>
                                </div>

                                <div className="flex flex-wrap items-center gap-4 mt-3 text-xs text-slate-500">
                                  <div className="flex items-center gap-1">
                                    <div className="w-3 h-3 bg-green-500/20 border border-green-500 rounded"></div>
                                    Normal range
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <div className="w-3 h-0.5 bg-blue-500"></div>
                                    Metric
                                  </div>
                                  {errorData.length > 0 && (
                                    <div className="flex items-center gap-1">
                                      <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
                                      Errors ({errorData.length})
                                    </div>
                                  )}
                                  {alertData.length > 0 && (
                                    <div className="flex items-center gap-1">
                                      <div className="w-2.5 h-2.5 bg-orange-500 rotate-45"></div>
                                      Alerts ({alertData.length})
                                    </div>
                                  )}
                                </div>
                              </div>
                            )
                          })()}

                          {/* Recent Errors */}
                          {(anomalyContext.recent_errors?.length ?? 0) > 0 && (
                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                              <h4 className="text-sm font-medium text-slate-900 mb-3">
                                Related Errors (±1h window)
                              </h4>
                              <div className="space-y-1">
                                {anomalyContext.recent_errors.map((error, i) => {
                                  const hourKey = getHourKey(error.timestamp)
                                  const isHovered = hoveredErrorHour === hourKey
                                  return (
                                    <div
                                      key={i}
                                      className={`flex items-center justify-between text-sm px-2 py-1.5 rounded-md cursor-pointer transition-all duration-150 ${
                                        isHovered
                                          ? 'bg-red-50 ring-2 ring-red-200'
                                          : 'hover:bg-slate-50'
                                      }`}
                                      onMouseEnter={() => setHoveredErrorHour(hourKey)}
                                      onMouseLeave={() => setHoveredErrorHour(null)}
                                      onClick={() =>
                                        navigate(
                                          `/t/${teamId}/apps/${anomalyContext.app_id}/errors/${error.id}`
                                        )
                                      }
                                    >
                                      <div className="flex items-center gap-2">
                                        <span
                                          className={`px-1.5 py-0.5 text-xs rounded ${
                                            error.level === 'error'
                                              ? 'bg-red-100 text-red-700'
                                              : error.level === 'warning'
                                                ? 'bg-yellow-100 text-yellow-700'
                                                : 'bg-slate-100 text-slate-700'
                                          }`}
                                        >
                                          {error.level}
                                        </span>
                                        <span className="text-slate-700 truncate max-w-md">
                                          {error.message}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 text-slate-500">
                                        <span>{error.count}x</span>
                                        <ChevronDown className="w-3 h-3 -rotate-90" />
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}

                          {/* Related Alerts */}
                          {(anomalyContext.alert_events?.length ?? 0) > 0 && (
                            <div className="bg-white rounded-lg p-4 border border-slate-200">
                              <h4 className="text-sm font-medium text-slate-900 mb-3">
                                Related Alerts (48h window)
                              </h4>
                              <div className="space-y-1">
                                {anomalyContext.alert_events.map((alert, i) => {
                                  const isHovered = hoveredAlertTimestamp === alert.timestamp
                                  const alertDate = new Date(alert.timestamp)
                                  return (
                                    <div
                                      key={i}
                                      className={`flex items-center justify-between text-sm px-2 py-1.5 rounded-md cursor-pointer transition-all duration-150 ${
                                        isHovered
                                          ? 'bg-orange-50 ring-2 ring-orange-200'
                                          : 'hover:bg-slate-50'
                                      }`}
                                      onMouseEnter={() => setHoveredAlertTimestamp(alert.timestamp)}
                                      onMouseLeave={() => setHoveredAlertTimestamp(null)}
                                      onClick={() => setActiveTab('alerts')}
                                    >
                                      <div className="flex items-center gap-2">
                                        <span
                                          className={`px-1.5 py-0.5 text-xs rounded ${getAlertTypeColor(alert.type)}`}
                                        >
                                          {formatAlertType(alert.type)}
                                        </span>
                                        <span className="text-slate-700 truncate max-w-md">
                                          {alert.title !== alert.type
                                            ? alert.title
                                            : `${formatAlertType(alert.type)} triggered`}
                                        </span>
                                      </div>
                                      <div className="flex items-center gap-2 text-slate-400 whitespace-nowrap">
                                        <span className="text-xs">
                                          {alertDate.toLocaleDateString('en-US', {
                                            month: 'short',
                                            day: 'numeric',
                                          })}{' '}
                                          {alertDate.toLocaleTimeString('en-US', {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}
                                        </span>
                                        <ChevronDown className="w-3 h-3 -rotate-90" />
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      ) : null}
                    </div>
                  )}
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Alerts Tab */}
      {activeTab === 'alerts' && (
        <div className="space-y-4">
          {alertsLoading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Card key={i} className="p-4">
                  <div className="flex items-center gap-4">
                    <Skeleton className="w-10 h-10 rounded-lg" />
                    <div className="flex-1">
                      <Skeleton className="h-5 w-64 mb-2" />
                      <Skeleton className="h-4 w-48" />
                    </div>
                    <Skeleton className="h-4 w-20" />
                  </div>
                </Card>
              ))}
            </div>
          ) : alertHistory.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Bell className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No alerts yet</p>
              <p className="text-sm mt-1">
                Alerts will appear here when we send notifications about your apps
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {alertHistory.map(alert => {
                  const appId = alert.metadata?.app_id || alert.resource_id
                  const errorGroupId = alert.metadata?.error_group_id
                  const clickable = !!appId

                  const handleClick = () => {
                    if (appId && errorGroupId) {
                      navigate(`/t/${teamId}/apps/${appId}/errors/${errorGroupId}`)
                    } else if (appId) {
                      navigate(`/t/${teamId}/apps/${appId}`)
                    }
                  }

                  const getAlertIcon = () => {
                    switch (alert.alert_type) {
                      case 'error':
                        return <AlertTriangle className="w-5 h-5 text-red-600" />
                      case 'critical':
                        return <XCircle className="w-5 h-5 text-red-700" />
                      case 'recovery':
                        return <CheckCircle className="w-5 h-5 text-green-600" />
                      case 'warning':
                        return <Activity className="w-5 h-5 text-yellow-600" />
                      default:
                        return <Bell className="w-5 h-5 text-slate-500" />
                    }
                  }

                  const getTypeBadge = () => {
                    const variants: Record<string, { label: string; className: string }> = {
                      error: { label: 'Error', className: 'bg-red-100 text-red-700' },
                      critical: { label: 'Critical', className: 'bg-red-200 text-red-800' },
                      recovery: { label: 'Recovery', className: 'bg-green-100 text-green-700' },
                      warning: { label: 'Warning', className: 'bg-yellow-100 text-yellow-700' },
                      info: { label: 'Info', className: 'bg-blue-100 text-blue-700' },
                    }
                    const v = variants[alert.alert_type] || {
                      label: alert.alert_type,
                      className: 'bg-slate-100 text-slate-700',
                    }
                    return (
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${v.className}`}
                      >
                        {v.label}
                      </span>
                    )
                  }

                  const getChannelIcon = (channel: string) => {
                    switch (channel) {
                      case 'email':
                        return <Mail className="w-3.5 h-3.5" />
                      case 'slack':
                        return <MessageSquare className="w-3.5 h-3.5" />
                      case 'discord':
                        return <MessageSquare className="w-3.5 h-3.5" />
                      case 'webhook':
                        return <Webhook className="w-3.5 h-3.5" />
                      default:
                        return <Bell className="w-3.5 h-3.5" />
                    }
                  }

                  const formatTime = (dateStr: string) => {
                    const date = new Date(dateStr)
                    const now = new Date()
                    const diffMs = now.getTime() - date.getTime()
                    const diffMins = Math.floor(diffMs / 60000)
                    const diffHours = Math.floor(diffMs / 3600000)
                    const diffDays = Math.floor(diffMs / 86400000)
                    if (diffMins < 1) return 'Just now'
                    if (diffMins < 60) return `${diffMins}m ago`
                    if (diffHours < 24) return `${diffHours}h ago`
                    if (diffDays < 7) return `${diffDays}d ago`
                    return date.toLocaleDateString()
                  }

                  return (
                    <Card
                      key={alert.id}
                      variant={clickable ? 'interactive' : 'default'}
                      className={`p-4 ${clickable ? 'cursor-pointer hover:border-slate-300' : ''}`}
                      onClick={clickable ? handleClick : undefined}
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          {getAlertIcon()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className="text-sm font-medium text-slate-900 truncate">
                              {alert.title}
                            </h3>
                            {clickable && (
                              <ExternalLink className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                            )}
                          </div>
                          <div className="text-sm text-slate-600 mb-2">
                            {alert.metadata?.app_name && (
                              <span className="font-medium text-slate-700">
                                {alert.metadata.app_name}
                              </span>
                            )}
                            {alert.metadata?.app_name && alert.message && (
                              <span className="mx-1.5 text-slate-400">·</span>
                            )}
                            <span className="truncate">
                              {alert.message.slice(0, 80)}
                              {alert.message.length > 80 ? '...' : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {getTypeBadge()}
                            <div className="flex items-center gap-1">
                              {alert.channels_sent.map(channel => (
                                <span
                                  key={channel}
                                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 text-xs"
                                  title={channel}
                                >
                                  {getChannelIcon(channel)}
                                </span>
                              ))}
                            </div>
                          </div>
                        </div>
                        <div className="flex-shrink-0 text-right">
                          <span className="text-sm text-slate-500">
                            {formatTime(alert.sent_at)}
                          </span>
                          <div className="text-xs text-slate-400 mt-0.5">
                            {new Date(alert.sent_at).toLocaleTimeString([], {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </div>
                        </div>
                      </div>
                    </Card>
                  )
                })}
              </div>

              {Math.ceil(alertsTotal / 20) > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-slate-600">
                    Showing {alertsPage * 20 + 1}-{Math.min((alertsPage + 1) * 20, alertsTotal)} of{' '}
                    {alertsTotal} alerts
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setAlertsPage(alertsPage - 1)}
                      disabled={alertsPage === 0}
                      className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => setAlertsPage(alertsPage + 1)}
                      disabled={alertsPage >= Math.ceil(alertsTotal / 20) - 1}
                      className="px-3 py-1.5 text-sm border border-slate-200 rounded-lg bg-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
